// react
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';

// api
import {
  usePutEnergyGroupEnergyTreeNodeOptionsMutation,
  usePutEnergyGroupMutation,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';
import { usePutV2LocationsByIdMutation } from '@/api/generated/recalc/endpoints/v2/locations';
import type { LocationDto } from '@/api/generated/recalc/endpoints/v2/locations';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import { RHFAddressAutocomplete, RHFTextField } from '@/components/ui/form';

// feature-components
import { OpeningHoursEditor } from '@/features/energyxplorer/components/tree/OpeningHoursEditor';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import type {
  LoadedEnergyGroup,
  NormalizedEnergyTreeNodeOptions,
} from './editEnergyGroupForm.mappers';
import {
  toEditEnergyGroupFormValues,
  toPutEnergyGroupDto,
  toPutEnergyTreeNodeOptionsDto,
  toPutLocationDto,
} from './editEnergyGroupForm.mappers';
import { createEditEnergyGroupFormSchema } from './editEnergyGroupForm.schema';

import { zodResolver } from '@hookform/resolvers/zod';

export default function EditEnergyGroupForm({
  energyGroup,
  location,
  normalizedNodeOptions,
  rootNodeId,
  onCancel,
}: {
  energyGroup: LoadedEnergyGroup;
  location: LocationDto;
  normalizedNodeOptions: NormalizedEnergyTreeNodeOptions;
  rootNodeId: string | number | null;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['forms', 'validation']);
  const dispatch = useDispatch();

  const [putEnergyGroup, { isLoading: isSavingGroup }] =
    usePutEnergyGroupMutation();
  const [putLocation, { isLoading: isSavingLocation }] =
    usePutV2LocationsByIdMutation();
  const [putEnergyTreeNodeOptions, { isLoading: isSavingOptions }] =
    usePutEnergyGroupEnergyTreeNodeOptionsMutation();
  const isSaving = isSavingGroup || isSavingLocation || isSavingOptions;

  const schema = createEditEnergyGroupFormSchema(t);
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty, dirtyFields },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toEditEnergyGroupFormValues(
      energyGroup,
      location,
      normalizedNodeOptions.openingHours,
    ),
    mode: 'onTouched',
  });

  const onSubmit = handleSubmit(async (values) => {
    if (dirtyFields.openingHours && !rootNodeId) {
      dispatch(
        showSnackbar({
          message: t('editEnergyGroup.missingRootNodeError', { ns: 'forms' }),
          severity: 'error',
        }),
      );
      return;
    }

    if (dirtyFields.name) {
      const result = await putEnergyGroup({
        groupId: energyGroup.energyGroupId,
        putEnergyGroupDto: toPutEnergyGroupDto(values, energyGroup),
      });
      if ('error' in result) return;
    }

    if (dirtyFields.location) {
      const result = await putLocation({
        id: energyGroup.locationId,
        baseLocationDto: toPutLocationDto(values),
      });
      if ('error' in result) return;
    }

    if (dirtyFields.openingHours && rootNodeId) {
      const result = await putEnergyTreeNodeOptions({
        groupId: energyGroup.energyGroupId,
        nodeId: String(rootNodeId),
        putEnergyTreeNodeOptionsDto: toPutEnergyTreeNodeOptionsDto(
          values,
          normalizedNodeOptions,
        ),
      });
      if ('error' in result) return;
    }

    dispatch(
      showSnackbar({
        message: t('common.updateSuccess'),
        severity: 'success',
      }),
    );

    reset(values);
    // Errors are handled by apiErrorMiddleware (snackbar + App Insights).
  });

  return (
    <form onSubmit={onSubmit} noValidate data-testid="energy-group-edit-form">
      <Stack spacing={2}>
        <Card variant="outlined" data-testid="energy-group-edit-card">
          <CardContent>
            <Stack spacing={2}>
              <RHFTextField
                name="name"
                control={control}
                required
                fullWidth
                label={t('editEnergyGroup.name.label', { ns: 'forms' })}
                placeholder={t('editEnergyGroup.name.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('editEnergyGroup.name.helperText', {
                  ns: 'forms',
                })}
                disabled={isSaving}
                data-testid="energy-group-edit-name-input"
              />

              <RHFAddressAutocomplete
                name="location"
                control={control}
                disabled={isSaving}
                required
                fullWidth
                size="small"
                label={t('editEnergyGroup.address.label', { ns: 'forms' })}
                placeholder={t('editEnergyGroup.address.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('editEnergyGroup.address.helperText', {
                  ns: 'forms',
                })}
                data-testid="energy-group-edit-address-input"
              />

              <Box>
                <Typography variant="body2" fontWeight={800} sx={{ mb: 1 }}>
                  {t('editEnergyGroup.openingHours.label', { ns: 'forms' })}
                </Typography>
                <Controller
                  name="openingHours"
                  control={control}
                  render={({ field }) => (
                    <OpeningHoursEditor
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t('editEnergyGroup.openingHours.helperText', {
                    ns: 'forms',
                  })}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton
            onClick={onCancel}
            disabled={isSaving}
            data-testid="energy-group-edit-cancel-button"
          >
            {t('common.cancel')}
          </CancelButton>
          <SubmitButton
            loading={isSaving}
            disabled={isSaving || !isValid || !isDirty}
            data-testid="energy-group-edit-save-button"
          >
            {isSaving
              ? t('editEnergyGroup.submitting', { ns: 'forms' })
              : t('editEnergyGroup.submit', { ns: 'forms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
